import { useFormikContext } from 'formik';
import useFormikError from 'hooks/useFormikError';
import { useEffect } from 'react';
import {
  ExclamationIcon,
  InformationCircleIcon,
} from '@heroicons/react/outline';
import classnames from 'classnames';
import { createSlug } from 'helpers';

interface Props {
  name: string;
  /**
   * If not provided, the label will be the same as the name
   */
  label?: JSX.Element | string;
  autocomplete?: {
    field: string;
    type?: 'slug';
  };
  defaultValue?: string | null;
  icon?: JSX.Element | string | number;
  slim?: boolean;
  helperText?: string | null;
  children: React.ReactNode
}

export const inputClass = (error: boolean, slim: boolean) =>
  classnames({
    'min-h-[42px] form-control block w-full px-2 py-1 text-input font-normal bg-white border border-solid border-input-border rounded transition ease-in-out m-0 focus:text-gray-700  focus:border-input-border-focus  focus:outline-none hover:border-input-border-hover ':
      true,
    'focus:shadow-input-focus': !error,
    'border-red-300 text-red-900 placeholder-red-300 focus:border-red-300 focus:shadow-outline-red':
      error,
  });

const InputBase: React.FC<Props> = ({
  label,
  name,
  children,
  autocomplete,
  icon,
  slim,
  defaultValue,
  helperText,
}) => {
  const error = useFormikError(name);
  const { values, setFieldValue, touched, getFieldMeta, getFieldProps } =
    useFormikContext();
  const { initialValue } = getFieldMeta(name);

  // @ts-ignore
  const refFieldValue = values[autocomplete?.field];
  useEffect(() => {
    // @ts-ignore
    if (autocomplete && !touched[name] && !initialValue) {
      if (autocomplete.type === 'slug') {
        setFieldValue(name, createSlug(refFieldValue));
      } else {
        setFieldValue(name, refFieldValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refFieldValue]);

  useEffect(() => {
    if (defaultValue) {
      const fieldProps = getFieldProps(name);
      if (!fieldProps.value) setFieldValue(name, defaultValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={slim ? 'relative mb-2 w-full' : 'relative my-4 w-full '}>
      {label && (
        <label
          htmlFor={name}
          className="block text-input leading-5 text-black mb-1"
        >
          {label}
        </label>
      )}
      <div className="relative rounded-md shadow-sm">
        {children}
        {error ? (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <ExclamationIcon className="w-5 h-5 text-red-600"></ExclamationIcon>
          </div>
        ) : icon ? (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            {icon}
          </div>
        ) : null}
      </div>
      {helperText && (
        <div className="text-sm py-2  flex gap-2 items-center">
          <div>
            <InformationCircleIcon className="h-4 w-4 text" />
          </div>
          <p>{helperText}</p>
        </div>
      )}
      {error && (
        <p
          className={`absolute right-0 ${
            label ? 'top-1' : 'top-11'
          } text-xs text-red-600 leading-none`}
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default InputBase;
